import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { B2bSalesDocumentModule, SalesDocumentAccessGuard, SalesDocumentComponent, SalesDocumentsListComponent } from '@icc/b2b/sales-document';
import { ComplaintsDocumentType, SalesDocumentType } from '@icc/b2b/shared';
import { NgxPermissionsGuard } from 'ngx-permissions';

import { RedirectComponent } from './redirect/redirect.component';
import { ComplaintB2cComponent } from 'libs/b2b/complaint/src/lib/complaint-b2c/complaint-b2c.component';
import { ComplaintsListComponent } from 'libs/b2b/complaint/src/lib/complaints-list/complaints-list.component';
import { ComplaintComponent } from 'libs/b2b/complaint/src/lib/complaint/complaint.component';
import { TransportComponent, TransportsListComponent } from '@icc/b2b/transport';
import { ComplaintsV1ListComponent, ComplaintV1Component } from '@icc/b2b/complaints-v1';
import { ComplaintAppointmentListComponent } from 'libs/b2b/complaint/src/lib/complaint-appointment-list/complaint-appointment-list.component';
import { ConsentGuard } from './consent.guard';
import { ConsentComponent } from './consent/consent.component';

const appRoutes: Routes = [
    {
        path: 'configurator',
        loadChildren: () => import('@icc/b2b/configurator').then((m) => m.B2BConfiguratorLoaderModule),
        canLoad: [NgxPermissionsGuard, ConsentGuard],
        data: {
            panel: 'configurator',
            permissions: {
                only: 'access-trade',
                redirectTo: {
                    navigationCommands: ['/iccadmin'],
                },
            },
        },
    },
    {
        path: 'trade/complaint-form',
        component: ComplaintB2cComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
            panel: 'complaint',
            isB2bComplaintForm: false,
            permissions: {
                only: ['access-complaintb2c', 'complaints-manage'],
                redirectTo: {
                    navigationCommands: ['/iccadmin'],
                },
            },
        },
    },
    {
        path: 'trade/complaint-form-internal',
        component: ComplaintB2cComponent,
        canActivate: [NgxPermissionsGuard],
        data: {
            panel: 'complaint',
            isB2bComplaintForm: true,
            permissions: {
                only: ['access-complaintb2c', 'complaints-manage'],
                redirectTo: {
                    navigationCommands: ['/iccadmin'],
                },
            },
        },
    },
    {
        path: 'trade/complaint',
        canLoad: [NgxPermissionsGuard],
        children: [
            {
                path: 'list/complaints',
                component: ComplaintsListComponent,
                data: {
                    complaintType: ComplaintsDocumentType.Complaint,
                    breadcrumb: 'Reklamacje',
                },
            },
            {
                path: 'list/appointments',
                component: ComplaintAppointmentListComponent,
                data: {
                    breadcrumb: 'Wizyty',
                },
            },
            {
                path: ':id',
                component: ComplaintComponent,
                canActivate: [SalesDocumentAccessGuard],
                data: {
                    breadcrumb: 'Reklamacja',
                },
            },
        ],
    },
    {
        path: 'trade/offer',
        canLoad: [NgxPermissionsGuard, ConsentGuard],
        children: [
            {
                path: 'list/offers',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Quotation,
                    breadcrumb: 'Oferty',
                },
            },
            {
                path: 'list/offers/selection',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Quotation,
                    mode: 'selection',
                    breadcrumb: 'Oferty',
                },
            },
            {
                path: 'list/orders/sales',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Order,
                    sales: true,
                    breadcrumb: 'Zamówienia sprzedażowe',
                },
            },
            {
                path: 'list/orders/purchase',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Order,
                    purchase: true,
                    breadcrumb: 'Zamówienia zakupowe',
                },
            },
            {
                path: 'list/orders/unplaced',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Order,
                    purchase: true,
                    unplaced: true,
                    breadcrumb: 'Zamówienia zakupowe do potwierdzenia',
                },
            },
            {
                path: 'list/service-orders',
                component: SalesDocumentsListComponent,
                data: {
                    documentType: SalesDocumentType.ServiceOrder,
                    breadcrumb: 'Zamówienia serwisowe',
                },
            },
            {
                path: 'list/inquiries/sales',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Inquiry,
                    sales: true,
                    breadcrumb: 'Zapytania ofertowe sprzedażowe',
                },
            },
            {
                path: 'list/inquiries/purchase',
                component: SalesDocumentsListComponent,
                canActivate: [ConsentGuard],
                data: {
                    documentType: SalesDocumentType.Inquiry,
                    purchase: true,
                    breadcrumb: 'Zapytania ofertowe zakupowe',
                },
            },
            {
                path: ':id',
                component: SalesDocumentComponent,
                canActivate: [SalesDocumentAccessGuard, ConsentGuard],
                data: {
                    breadcrumb: 'Oferta',
                },
            },
            {
                path: "",
                redirectTo: '/list/offers',
                pathMatch: 'full'
            }
        ],
        data: {
            panel: 'trade',
            canActivate: [ConsentGuard],
            permissions: {
                only: 'access-trade',
                redirectTo: {
                    navigationCommands: ['/iccadmin'],
                },
            },
        },
    },
    {
        path: 'transport/transports',
        component: TransportsListComponent,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Lista zgłoszeń transportów',
        },
    },
    {
        path: 'transport/transport/new',
        component: TransportComponent,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Zgłoszenie transportu',
        },
    },
    {
        path: 'transport/transport/complaint/:complaintId',
        component: TransportComponent,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Zgłoszenie transportu',
        },
    },
    {
        path: 'transport/transport/:id',
        component: TransportComponent,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Zgłoszenie transportu',
        },
    },
    {
        path: 'complaint-v1/complaints',
        component: ComplaintsV1ListComponent,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Lista zgłoszeń reklamacji',
        },
    },
    {
        path: 'complaint-v1/complaint/new/:salesDocumentId/:salesDocumentPositionId',
        component: ComplaintV1Component,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Zgłoszenie reklamacji',
        },
    },
    {
        path: 'complaint-v1/complaint/new/:salesDocumentId',
        component: ComplaintV1Component,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Zgłoszenie reklamacji',
        },
    },
    {
        path: 'complaint-v1/complaint/:id',
        component: ComplaintV1Component,
        canActivate: [ConsentGuard],
        data: {
            breadcrumb: 'Zgłoszenie reklamacji',
        },
    },
    {
        path: 'trade',
        loadChildren: () => import('@icc/b2b/trade').then((m) => m.B2bTradeModule),
        canLoad: [NgxPermissionsGuard, ConsentGuard],
        data: {
            panel: 'trade',
            permissions: {
                only: 'access-trade',
                redirectTo: {
                    navigationCommands: ['/iccadmin'],
                },
            },
        },
    },
    {
        path: 'consent',
        component: ConsentComponent,
        data: {
            panel: 'trade',
            permissions: {
                only: 'access-trade',
                redirectTo: {
                    navigationCommands: ['/iccadmin'],
                },
            },
        },
    },
    {
        path: 'iccadmin',
        component: RedirectComponent,
        data: {
            externalUrl: '/iccadmin',
        },
    },
    {
        path: 'complaintb2c',
        redirectTo: 'trade/complaint-form',
    },
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'trade',
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(
            appRoutes,
            {
    initialNavigation: 'enabledNonBlocking',
    enableTracing: false
} // <-- debugging purposes only
 // <-- debugging purposes only
        ),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
